import { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import ConditionalTooltip from "@components/ConditionalTooltip";
import styles from "@components/Specialties.module.css";
import { ExpertFragment as Expert } from "@typing/Generated";

type Props = {
  expert: Expert;
  showAll?: boolean;
};

enum Kinds {
  AGE = "age",
  CONDITION = "condition",
  OTHER = "other"
}

const Specialties = ({ expert, showAll }: Props) => {
  const intl = useIntl();

  const specialties = useMemo(() => {
    const items: { kind: Kinds; label: string }[] = [];

    expert.specialtyAges.forEach(option =>
      items.push({
        kind: Kinds.AGE,
        label: intl.formatMessage({ id: `models.specialties.age.${option}` })
      })
    );
    expert.specialtyConditions.forEach(option =>
      items.push({
        kind: Kinds.CONDITION,
        label: intl.formatMessage({ id: `models.specialties.condition.${option}` })
      })
    );
    expert.specialtyOthers.forEach(option =>
      items.push({
        kind: Kinds.OTHER,
        label: intl.formatMessage({ id: `models.specialties.other.${option}` })
      })
    );

    return {
      allItems: items,
      items: showAll ? items : items.slice(0, 3),
      length: items.length
    };
  }, [expert.specialtyAges, expert.specialtyConditions, expert.specialtyOthers, intl, showAll]);

  return (
    <ConditionalTooltip
      condition={!showAll}
      content={
        <div className={styles.specialties}>
          {specialties.allItems.map(specialty => (
            <div className={styles.specialty + " " + styles[specialty.kind]} key={specialty.label}>
              {specialty.label}
            </div>
          ))}
        </div>
      }
    >
      {tooltipTriggerProps => (
        <div className={styles.specialties} tabIndex={tooltipTriggerProps && 0} {...tooltipTriggerProps}>
          {specialties.items.map(specialty => (
            <div className={styles.specialty + " " + styles[specialty.kind]} key={specialty.label}>
              {specialty.label}
            </div>
          ))}
          {specialties.length > 3 && !showAll && (
            <div className={styles.more}>
              <FormattedMessage id="dictionary.punctuation.plus" />
              {specialties.length - 3}
            </div>
          )}
        </div>
      )}
    </ConditionalTooltip>
  );
};

export default Specialties;
